/**
 * Utility Functions
 * 工具函数模块
 * 
 * @description 提供通用的工具函数，包括用户权限检查、格式化等
 * @version 1.0.0
 */

const DealerThemeUtils = (function() {
  'use strict';

  /**
   * 检查用户是否已登录
   * @returns {boolean} 是否已登录
   */
  function isLoggedIn() {
    return typeof window.Shopify !== 'undefined' && 
           window.Shopify.customer !== null &&
           typeof window.Shopify.customer !== 'undefined';
  }

  /**
   * 检查用户是否为 Dealer（经销商）
   * @returns {boolean} 是否为 Dealer 用户
   */
  function isDealerUser() {
    if (!isLoggedIn()) {
      return false;
    }

    const customer = window.Shopify.customer;
    const dealerTag = window.themeSettings?.dealerTagName || 'dealer';
    
    return customer.tags && customer.tags.includes(dealerTag);
  }

  /**
   * 获取当前客户信息
   * @returns {Object|null} 客户信息对象或 null
   */
  function getCustomer() {
    if (!isLoggedIn()) {
      return null;
    }
    return window.Shopify.customer;
  }

  /**
   * 格式化价格
   * @param {number} cents - 价格（分为单位）
   * @param {string} format - 格式化模板
   * @returns {string} 格式化后的价格字符串
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  /**
   * 防抖函数
   * @param {Function} func - 需要防抖的函数
   * @param {number} wait - 等待时间（毫秒）
   * @returns {Function} 防抖后的函数
   */
  function debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * 节流函数
   * @param {Function} func - 需要节流的函数
   * @param {number} limit - 时间限制（毫秒）
   * @returns {Function} 节流后的函数
   */
  function throttle(func, limit = 300) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * 获取 URL 参数
   * @param {string} param - 参数名
   * @returns {string|null} 参数值或 null
   */
  function getUrlParameter(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  /**
   * 设置 Cookie
   * @param {string} name - Cookie 名称
   * @param {string} value - Cookie 值
   * @param {number} days - 过期天数
   */
  function setCookie(name, value, days = 30) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
  }

  /**
   * 获取 Cookie
   * @param {string} name - Cookie 名称
   * @returns {string|null} Cookie 值或 null
   */
  function getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /**
   * 显示通知消息
   * @param {string} message - 消息内容
   * @param {string} type - 消息类型 (success, error, warning, info)
   */
  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    
    document.body.appendChild(notification);
    
    // 添加显示动画
    setTimeout(() => {
      notification.classList.add('notification--show');
    }, 10);
    
    // 3秒后自动移除
    setTimeout(() => {
      notification.classList.remove('notification--show');
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  /**
   * 懒加载图片
   * @param {string} selector - 图片选择器
   */
  function lazyLoadImages(selector = '[data-lazy-load]') {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const src = img.dataset.src;
            const srcset = img.dataset.srcset;
            
            if (src) {
              img.src = src;
            }
            if (srcset) {
              img.srcset = srcset;
            }
            
            img.classList.add('loaded');
            observer.unobserve(img);
          }
        });
      });

      document.querySelectorAll(selector).forEach(img => {
        imageObserver.observe(img);
      });
    } else {
      // 降级方案：直接加载所有图片
      document.querySelectorAll(selector).forEach(img => {
        const src = img.dataset.src;
        const srcset = img.dataset.srcset;
        if (src) img.src = src;
        if (srcset) img.srcset = srcset;
        img.classList.add('loaded');
      });
    }
  }

  /**
   * 平滑滚动到元素
   * @param {string|Element} target - 目标元素或选择器
   * @param {number} offset - 偏移量
   */
  function scrollToElement(target, offset = 0) {
    const element = typeof target === 'string' 
      ? document.querySelector(target) 
      : target;
    
    if (!element) return;
    
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  /**
   * 检查元素是否在视口内
   * @param {Element} element - DOM 元素
   * @returns {boolean} 是否在视口内
   */
  function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  /**
   * 获取断点（用于响应式逻辑）
   * @returns {string} 当前断点 (mobile, tablet, desktop)
   */
  function getCurrentBreakpoint() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  /**
   * 锁定/解锁页面滚动
   * @param {boolean} lock - true 锁定，false 解锁
   */
  function toggleBodyScroll(lock) {
    if (lock) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${getScrollbarWidth()}px`;
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }

  /**
   * 获取滚动条宽度
   * @returns {number} 滚动条宽度
   */
  function getScrollbarWidth() {
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    
    const inner = document.createElement('div');
    outer.appendChild(inner);
    
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    outer.parentNode.removeChild(outer);
    
    return scrollbarWidth;
  }

  // 公开 API
  return {
    isLoggedIn,
    isDealerUser,
    getCustomer,
    formatMoney,
    debounce,
    throttle,
    getUrlParameter,
    setCookie,
    getCookie,
    showNotification,
    lazyLoadImages,
    scrollToElement,
    isInViewport,
    getCurrentBreakpoint,
    toggleBodyScroll,
    getScrollbarWidth
  };
})();

// 暴露到全局
window.DealerThemeUtils = DealerThemeUtils;

